import { useEffect, useState, useCallback } from 'react';
import { streamFlowApi } from '@lib/streamFlowApi';
import { HealthDot, Tag, ago } from './adminUi';

/**
 * Live egress board: the three exit nodes (gas hub / tk-bakery / azure) with
 * health + load, and per-provider the ordered chain with the CURRENT active
 * hop highlighted (and shifted hops struck through). Lets the operator see
 * "gas hub down → providers shifted to tk-bakery" at a glance, and watch the
 * chain when EGRESS_DEFAULT_CHAIN is activated. Read-only; polled by the
 * parent's refresh.
 */
type EgressData = NonNullable<Awaited<ReturnType<typeof streamFlowApi.adminEgress>>['data']>;

export default function EgressBoard() {
  const [data, setData] = useState<EgressData | null>(null);

  const refresh = useCallback(async () => {
    const res = await streamFlowApi.adminEgress();
    if (res.success && res.data) setData(res.data);
  }, []);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(t);
  }, [refresh]);

  if (!data) return null;
  const nodeName = (id: string) => data.nodes.find((n) => n.id === id)?.name ?? `${id.slice(0, 8)}…`;

  return (
    <section className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
        <h2 className="font-semibold">Egress chain</h2>
        <span className="text-xs text-gray-500">{data.nodes.length} nodes</span>
      </div>

      {data.failover_summary.length > 0 && (
        <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-700">
          {data.failover_summary.map((s, i) => <div key={i}>⚠ {s}</div>)}
        </div>
      )}

      {/* Nodes */}
      <table className="w-full text-sm">
        <tbody>
          {data.nodes.map((n) => (
            <tr key={n.id} className="border-t border-gray-100">
              <td className="px-3 py-2 w-6"><HealthDot status={n.health_status} /></td>
              <td className="px-3 py-2">
                <div className="font-medium">{n.name} {n.is_tailnet_only && <Tag>tailnet</Tag>}</div>
                <div className="text-xs text-gray-400 font-mono break-all">{n.host}</div>
              </td>
              <td className="px-3 py-2 text-right text-xs text-gray-500 whitespace-nowrap">
                {n.bound_provider_count} bound · {n.active_stream_count} live
              </td>
              <td className="px-3 py-2 text-right text-xs text-gray-400 whitespace-nowrap">{ago(n.last_health_check)}</td>
            </tr>
          ))}
          <tr className="border-t border-gray-100 bg-gray-50/50">
            <td className="px-3 py-2 w-6"><span className="inline-block h-2.5 w-2.5 rounded-full bg-sky-400" /></td>
            <td className="px-3 py-2"><div className="font-medium">azure-edge <span className="text-xs text-gray-400">(public tail)</span></div></td>
            <td className="px-3 py-2 text-right text-xs text-gray-500 whitespace-nowrap">{data.azure_edge.active_stream_count} live</td>
            <td />
          </tr>
        </tbody>
      </table>

      {/* Per-provider chain */}
      <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 text-xs font-semibold text-gray-600">Providers → active egress</div>
      <table className="w-full text-sm">
        <tbody>
          {data.providers.map((p) => (
            <tr key={p.id} className="border-t border-gray-100">
              <td className="px-3 py-2 w-6"><HealthDot status={p.health_status} /></td>
              <td className="px-3 py-2">
                <span className="font-medium">{p.name}</span>
                {p.shifted && <Tag tone="amber">shifted</Tag>}
              </td>
              <td className="px-3 py-2 text-right text-xs whitespace-nowrap">
                {p.egress_chain && p.egress_chain.length > 0 ? (
                  <span className="font-mono">
                    {p.egress_chain.map((id, i) => (
                      <span key={id}>
                        {i > 0 && <span className="text-gray-300"> → </span>}
                        <span className={id === p.active_egress ? 'text-emerald-700 font-semibold' : 'text-gray-400 line-through'}>
                          {nodeName(id)}
                        </span>
                      </span>
                    ))}
                    <span className="text-gray-300"> → </span>
                    <span className={p.active_egress === 'azure-edge' ? 'text-sky-700 font-semibold' : 'text-gray-400'}>azure</span>
                  </span>
                ) : (
                  <span className="text-gray-400">{p.active_egress === 'azure-edge' ? 'azure-edge' : nodeName(p.active_egress)}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
